import { PlusIcon, UpdateIcon } from '@radix-ui/react-icons';
import { Box, Flex, Grid } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTableButton } from 'components/common/table/button';
import { MachineSubscriptionButton } from 'components/sections/admin-portal/machines/machine-subscription-button';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { TeamsContext } from 'contexts/admin/teams.context';
import { CheckedContext } from 'contexts/layout/checked.context';
import { lightFormat, parseISO } from 'date-fns';
import {
  AdminPortalDialog,
  getMachineStatusDisplay,
  getMachineStatusGroup,
  MachinesView,
} from 'enums/admin-portal';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { MachineStatus } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { useContext, useMemo } from 'react';
import { TrainingLogsService } from 'services/training-logs.service';
import { useAuthStore } from 'stores/use-auth-store';

const ENABLE_FILTER_CREATED_DATE = false;

interface IOptionsDict {
  _created: string[];
  machines: IOption[];
  firmware: string[];
}

export const MachinesTableToolbar = () => {
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const teamsCx = useContext(TeamsContext);
  const { resetChecked } = useContext(CheckedContext);

  const loading = useMachinesPortalStore((s) => s.loading);
  const viewMode = useMachinesPortalStore((s) => s.viewMode);
  const machines = useMachinesPortalStore((s) => s.machines);
  const filters = useMachinesPortalStore((s) => s.filters);
  const openDialog = useMachinesPortalStore((s) => s.openDialog);
  const mergeMachineFilters = useMachinesPortalStore(
    (s) => s.mergeMachineFilters
  );
  const setTpSummaryDict = useMachinesPortalStore((s) => s.setTpSummaryDict);
  const setViewMode = useMachinesPortalStore((s) => s.setViewMode);

  const options = useMemo(() => {
    const output: IOptionsDict = {
      _created: ArrayHelper.unique(
        machines.map((m) =>
          lightFormat(parseISO(m._created), LOCAL_DATE_FORMAT)
        )
      ),

      machines: machines.map((m) => {
        const o: IOption = {
          label: MachineContextHelper.getMachineMenuLabel(m),
          value: m.machineID,
        };
        return o;
      }),

      firmware: ArrayHelper.unique(
        machines.map((m) => m.last_firmware?.split(' ')[0] as string)
      ),
    };

    return output;
  }, [machines]);

  const dateOptions = useMemo(
    () =>
      options._created.map((m) => {
        const o: IOption = {
          label: m,
          value: m,
        };

        return o;
      }),
    [options._created]
  );

  const firmwareOptions = useMemo(
    () =>
      options.firmware.map((m) => {
        const o: IOption = {
          label: m,
          value: m,
        };

        return o;
      }),
    [options.firmware]
  );

  return (
    <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
      <Grid
        columns={ENABLE_FILTER_CREATED_DATE ? '9' : '8'}
        gap={RADIX.FLEX.GAP.SM}
        flexGrow="1"
      >
        <Box>
          <CommonSelectInput
            id="machines-viewMode"
            name="machines-viewMode"
            placeholder="View Mode"
            variant="soft"
            inputColor={RADIX.COLOR.NEUTRAL}
            options={Object.values(MachinesView).map((o) => ({
              label: o,
              value: o,
            }))}
            value={viewMode}
            onChange={(v) => {
              setViewMode(v as MachinesView);
            }}
          />
        </Box>
        <Box>
          <CommonSearchInput
            id="machines-machineID"
            name="machines-machineID"
            placeholder="common.machine"
            options={options.machines}
            values={filters.machineIDs}
            onChange={(v) => {
              resetChecked();
              mergeMachineFilters({
                machineIDs: v,
              });
            }}
            multiple
            optional
          />
        </Box>
        <Box>
          <CommonSelectInput
            id="machines-connected"
            name="machines-connected"
            placeholder="Connected"
            options={[
              { label: 'Offline', value: 'false' },
              { label: 'Online', value: 'true' },
            ]}
            value={filters.isConnected?.toString()}
            onOptionalBooleanChange={(v) => {
              resetChecked();
              mergeMachineFilters({ isConnected: v });
            }}
            optional
          />
        </Box>
        <Box>
          <CommonSearchInput
            id="machines-status"
            name="machines-status"
            placeholder="Status"
            options={Object.values(MachineStatus).map((o) => ({
              label: getMachineStatusDisplay(o),
              group: getMachineStatusGroup(o),
              value: o,
            }))}
            values={filters.statuses}
            onChange={(v) => {
              resetChecked();
              mergeMachineFilters({ statuses: v as MachineStatus[] });
            }}
            multiple
            optional
          />
        </Box>
        <Box>
          <CommonSearchInput
            id="machines-team"
            name="machines-team"
            placeholder="common.team"
            options={teamsCx.options.teams}
            values={filters.teams}
            onChange={(v) => {
              resetChecked();
              mergeMachineFilters({
                teams: v,
              });
            }}
            multiple
            optional
          />
        </Box>

        {ENABLE_FILTER_CREATED_DATE && (
          <Box>
            <CommonSearchInput
              id="machines-created"
              name="machines-created"
              placeholder="common.date-added"
              options={dateOptions}
              values={filters.created}
              onChange={(v) => {
                resetChecked();
                mergeMachineFilters({ created: v });
              }}
              multiple
              reverseSort
              optional
            />
          </Box>
        )}

        <Box>
          <CommonSearchInput
            id="machines-firmware"
            name="machines-firmware"
            placeholder="Firmware"
            options={firmwareOptions}
            values={filters.firmware}
            onChange={(v) => {
              resetChecked();
              mergeMachineFilters({ firmware: v });
            }}
            multiple
            optional
          />
        </Box>
        <Box>
          <CommonSelectInput
            id="machines-active-user"
            name="machines-active-user"
            placeholder="common.active-user"
            options={[
              { label: 'No', value: 'false' },
              { label: 'Yes', value: 'true' },
            ]}
            value={filters.hasActive?.toString()}
            onOptionalBooleanChange={(v) => {
              resetChecked();
              mergeMachineFilters({ hasActive: v });
            }}
            optional
          />
        </Box>
        {userRoleFeatureFlags.adminMachines.toggleDeleted && (
          <Box>
            <CommonSelectInput
              id="machines-deleted"
              name="machines-deleted"
              placeholder="Active / Inactive"
              options={[
                { label: 'Inactive', value: 'true' },
                { label: 'Active', value: 'false' },
              ]}
              value={filters.isDeleted.toString()}
              onBooleanChange={(v) => {
                resetChecked();
                mergeMachineFilters({ isDeleted: v });
              }}
            />
          </Box>
        )}
      </Grid>
      <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
        {viewMode === MachinesView.DefaultView && (
          <MachineSubscriptionButton type="summary" />
        )}

        {viewMode === MachinesView.TrainingPerformance && (
          <CommonTableButton
            color={RADIX.COLOR.NEUTRAL}
            icon={<UpdateIcon />}
            label="common.refresh"
            disabled={loading}
            onClick={() => {
              TrainingLogsService.getInstance()
                .summarize()
                .then((result) => {
                  if (!result) {
                    NotifyHelper.error({
                      message_md: 'Failed to load training log summaries.',
                    });
                    return;
                  }

                  NotifyHelper.success({
                    message_md: 'Refreshed training log summaries!',
                  });

                  setTpSummaryDict(result);
                });
            }}
          />
        )}

        {userRoleFeatureFlags.adminMachines.create && (
          <CommonTableButton
            color={RADIX.COLOR.SUCCESS}
            icon={<PlusIcon />}
            label="common.create"
            disabled={loading}
            onClick={() =>
              openDialog({
                type: AdminPortalDialog.EditMachine,
              })
            }
          />
        )}
      </Flex>
    </Flex>
  );
};
